import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { get, patch, put } from '@/api/api.client';
import { Button } from '@/components/primitives/button';
import { Input } from '@/components/primitives/input';
import { showErrorToast, showSuccessToast } from '@/components/primitives/sonner-helpers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/primitives/tabs';
import { QueryKeys } from '@/utils/query-keys';
import { ROUTES } from '@/utils/routes';

type User = { firstName?: string | null; lastName?: string | null; email?: string | null };
type Organization = { name: string };
type Member = {
  _id: string;
  user?: { firstName?: string; lastName?: string; email?: string };
  invite?: { email: string };
  memberStatus?: string;
};

export function CommunitySettingsTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tab =
    location.pathname === ROUTES.SETTINGS_ORGANIZATION
      ? 'organization'
      : location.pathname === ROUTES.SETTINGS_TEAM
        ? 'team'
        : 'account';
  const user = useQuery({
    queryKey: ['community-settings-user'],
    queryFn: async () => (await get<{ data: User }>('/users/me')).data,
  });
  const organization = useQuery({
    queryKey: [QueryKeys.myOrganization],
    queryFn: async () => (await get<{ data: Organization }>('/organizations/me')).data,
  });
  const members = useQuery({
    queryKey: ['community-settings-members'],
    queryFn: async () => (await get<{ data: Member[] }>('/organizations/members')).data,
    enabled: tab === 'team',
  });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFirstName(user.data?.firstName ?? '');
    setLastName(user.data?.lastName ?? '');
  }, [user.data]);

  useEffect(() => {
    setOrganizationName(organization.data?.name ?? '');
  }, [organization.data]);

  async function saveAccount(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await put('/users/profile', { body: { firstName: firstName.trim(), lastName: lastName.trim() } });
      await queryClient.invalidateQueries({ queryKey: ['community-settings-user'] });
      showSuccessToast('Account updated');
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Could not update account');
    } finally {
      setSaving(false);
    }
  }

  async function saveOrganization(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await patch('/organizations/', { body: { name: organizationName.trim() } });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.myOrganization] });
      showSuccessToast('Organization updated');
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Could not update organization');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Tabs
      value={tab}
      onValueChange={(value) =>
        navigate(
          value === 'organization'
            ? ROUTES.SETTINGS_ORGANIZATION
            : value === 'team'
              ? ROUTES.SETTINGS_TEAM
              : ROUTES.SETTINGS_ACCOUNT
        )
      }
      className="-mx-2 w-full"
    >
      <TabsList align="center" variant="regular" className="border-t-transparent py-0!">
        <TabsTrigger variant="regular" value="account" size="xl">Account</TabsTrigger>
        <TabsTrigger variant="regular" value="organization" size="xl">Organization</TabsTrigger>
        <TabsTrigger variant="regular" value="team" size="xl">Team</TabsTrigger>
      </TabsList>

      <div className="mx-auto mt-6 max-w-[700px] px-4">
        <TabsContent value="account">
          <h2 className="mb-4 text-xl font-semibold text-text-strong">Account</h2>
          {user.isLoading ? <p>Loading account…</p> : user.isError ? <p>Could not load account.</p> : (
            <form onSubmit={saveAccount} className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium" htmlFor="community-first-name">First name</label>
                <Input id="community-first-name" value={firstName} onChange={(event) => setFirstName(event.target.value)} required />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" htmlFor="community-last-name">Last name</label>
                <Input id="community-last-name" value={lastName} onChange={(event) => setLastName(event.target.value)} required />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium" htmlFor="community-email">Email</label>
                <Input id="community-email" value={user.data?.email ?? ''} readOnly />
              </div>
              <Button type="submit" isLoading={saving} disabled={!firstName.trim() || !lastName.trim()}>Save changes</Button>
            </form>
          )}
        </TabsContent>

        <TabsContent value="organization">
          <h2 className="mb-4 text-xl font-semibold text-text-strong">Organization</h2>
          {organization.isLoading ? <p>Loading organization…</p> : organization.isError ? <p>Could not load organization.</p> : (
            <form onSubmit={saveOrganization} className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium" htmlFor="community-organization-name">Name</label>
                <Input id="community-organization-name" value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} required />
              </div>
              <Button type="submit" isLoading={saving} disabled={!organizationName.trim()}>Save changes</Button>
            </form>
          )}
        </TabsContent>

        <TabsContent value="team">
          <h2 className="mb-4 text-xl font-semibold text-text-strong">Team</h2>
          {members.isLoading ? <p>Loading team…</p> : members.isError ? <p>Could not load team.</p> : (
            <ul className="divide-y divide-neutral-200 rounded-xl border border-neutral-200">
              {members.data?.map((member) => (
                <li key={member._id} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                  <span className="font-medium text-text-strong">{[member.user?.firstName, member.user?.lastName].filter(Boolean).join(' ') || member.invite?.email || 'Team member'}</span>
                  <span className="text-text-sub">{member.user?.email || member.memberStatus || ''}</span>
                </li>
              ))}
            </ul>
          )}
        </TabsContent>
      </div>
    </Tabs>
  );
}
